import { useMutation, useQueryClient } from "@tanstack/react-query";
import localforage from "localforage";
import { AuthResults, getAuthQueryKey } from "./use-auth-cache";
import { LoginPayloadFragment } from "./fragments/login-payload";
import { useGetCurrentVenueStaffsOffline } from "~/queries/use-get-current-venue-staffs";
import { gqlReq } from "~/libs/gql";
import {
	LoginDocument,
	LoginMutation,
	LoginMutationVariables,
	LoginSilentDocument,
	LoginSilentMutation,
} from "~/types/__generated/gql/graphql";
import { useSetToken, useStore } from "~/store";
import { STORAGE_KEYS } from "~/libs/storage-keys";

void (
	/* GraphQL */ `
		mutation Login($input: LoginInput!) {
			login(input: $input) {
				token
				refreshToken
				role {
					_id
					type
					username
					first_name
					last_name
					position
					permissions
				}
			}
		}
	`
);

void (
	/* GraphQL */ `
		mutation LoginSilent($refreshToken: String!) {
			loginSilent(refreshToken: $refreshToken) {
				...LoginPayloadFragment
			}
		}
		${LoginPayloadFragment}
	`
);

export type LoginResults =
	| GqlResultOf<LoginMutation>
	| GqlResultOf<LoginSilentMutation>;

export const useLogin = () => {
	const queryClient = useQueryClient();

	const setToken = useSetToken();
	const setRefreshToken = useStore((s) => s.setRefreshToken);
	const setStaffToken = useStore((s) => s.setStaffToken);

	return useMutation({
		mutationFn: (variables: LoginMutationVariables) =>
			gqlReq({
				document: LoginDocument,
				variables,
			}),
		onSuccess: async (data) => {
			setToken(data.token);
			setRefreshToken(data.refreshToken);
			setStaffToken(data.role._id);
			queryClient.setQueryData<AuthResults>(getAuthQueryKey(), data.role);
			await localforage.setItem(STORAGE_KEYS.TOKEN, data.token);
			await localforage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);
			await localforage.setItem(
				STORAGE_KEYS.LAST_LOGGED_IN_ROLE_ID,
				data.role._id,
			);
			return data;
		},
	});
};

export const useLoginOffline = () => {
	const queryClient = useQueryClient();

	const { data: staffs = [] } = useGetCurrentVenueStaffsOffline();
	const setStaffToken = useStore((s) => s.setStaffToken);

	return useMutation({
		// eslint-disable-next-line @typescript-eslint/require-await
		mutationFn: async ({
			input: { venue_issued_id },
		}: LoginMutationVariables) => {
			if (!venue_issued_id) throw new Error("Invalid parameters");

			const staff = staffs.find((s) => s.venue_issued_id === venue_issued_id);
			if (!staff) throw new Error("User not found");

			setStaffToken(staff._id);
			queryClient.setQueryData<AuthResults>(getAuthQueryKey(), staff);
			await localforage.removeItem(STORAGE_KEYS.TOKEN);
			await localforage.setItem(STORAGE_KEYS.LAST_LOGGED_IN_ROLE_ID, staff._id);
		},
	});
};

export const useLoginSilent = () => {
	const queryClient = useQueryClient();

	const setToken = useSetToken();
	const setStaffToken = useStore((s) => s.setStaffToken);

	return useMutation({
		mutationFn: async () => {
			const storedRefreshToken = await localforage.getItem<string>(
				STORAGE_KEYS.REFRESH_TOKEN,
			);
			// If no refresh token, this is a legacy session - force re-login
			if (!storedRefreshToken) {
				console.info("[auth] legacy session detected, forcing re-login");
				await localforage.removeItem(STORAGE_KEYS.TOKEN);
				throw new Error("No refresh token - please login again");
			}
			return gqlReq({
				document: LoginSilentDocument,
				variables: { refreshToken: storedRefreshToken },
			});
		},
		onSuccess: async (data) => {
			setToken(data.token);
			setStaffToken(data.role._id);
			queryClient.setQueryData<AuthResults>(getAuthQueryKey(), data.role);
			await localforage.setItem(STORAGE_KEYS.TOKEN, data.token);
			await localforage.setItem(
				STORAGE_KEYS.LAST_LOGGED_IN_ROLE_ID,
				data.role._id,
			);
		},
	});
};

export const useLoginSilentOffline = () => {
	const queryClient = useQueryClient();

	const { data: staffs = [] } = useGetCurrentVenueStaffsOffline();
	const setStaffToken = useStore((s) => s.setStaffToken);

	return useMutation({
		// eslint-disable-next-line @typescript-eslint/require-await
		mutationFn: async () => {
			const roleId = await localforage.getItem<string>(
				STORAGE_KEYS.LAST_LOGGED_IN_ROLE_ID,
			);
			if (!roleId) throw new Error("No offline staff found");

			const staff = staffs.find((s) => s._id === roleId);
			if (!staff) throw new Error("User not found");

			setStaffToken(staff._id);
			queryClient.setQueryData<AuthResults>(getAuthQueryKey(), staff);

			await localforage.removeItem(STORAGE_KEYS.TOKEN);
			await localforage.setItem(STORAGE_KEYS.LAST_LOGGED_IN_ROLE_ID, staff._id);
		},
	});
};
